/**
 * NBCOlympicsBadge — trust signal card with click-to-view-letter modal.
 *
 * Shows a tappable NBC Tokyo 2020 vendor badge. Clicking it opens an
 * inline modal that displays the official recommendation letter from
 * Richard Berrigan (NBC Olympics Team Manager · Ground Transportation)
 * confirming JapanETCcard's role as a fleet ETC vendor for the 2020
 * Tokyo Summer Games. Used on the Testimonials page hero and homepage.
 *
 * Props:
 *   - lang: "en" | "ja"
 *   - placement: "hero" | "row" — adjusts spacing.
 *   - testId: optional id suffix to scope data-testids.
 *
 * Self-contained modal (no shadcn dependency). Backdrop click + ESC close.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

// File names swapped vs. expected: `nbc-letter.png` turned out to be the
// peacock logo (use as the click trigger), and `nbc-tokyo-2020.png` is the
// recommendation letter scan (use inside the modal). Owner will re-upload
// a higher-res letter if needed; until then this swap renders the
// expected behavior (click peacock → see letter).
const NBC_BADGE_SRC = "/badges/nbc/nbc-letter.png";
const NBC_LETTER_SRC = "/badges/nbc/nbc-tokyo-2020.png";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

export default function NBCOlympicsBadge({ lang = "en", placement = "hero", testId = "nbc" }) {
  const [open, setOpen] = useState(false);
  const isJa = lang === "ja";

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Icon-with-summary design: small Tokyo 2020 peacock on the left,
          short trust-text on the right. The icon and the text are both
          click targets that open the letter modal. A hover tooltip
          surfaces the "click to view official letter" hint — anchored
          to the icon (not the full cluster) and rendered ABOVE so it
          never bleeds past the bottom of the host section. */}
      <div
        className="relative inline-flex items-center gap-3"
        data-testid={`${testId}-badge`}
      >
        <span className="group relative inline-flex">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={`${testId}-badge-trigger`}
            aria-label={
              isJa
                ? "NBC Tokyo 2020 公式オリンピックベンダー — 公式レターを開く"
                : "NBC Tokyo 2020 Official Olympics Vendor — open the official letter"
            }
            className={[
              "shrink-0 relative inline-flex items-center justify-center rounded-xl bg-white ring-2 ring-[#E2E8F0] hover:ring-[#5B2E83] hover:-translate-y-0.5 hover:shadow-[0_16px_35px_-18px_rgba(91,46,131,0.45)] transition-all cursor-pointer overflow-hidden",
              placement === "hero"
                ? "h-16 w-16 md:h-20 md:w-20"
                : "h-12 w-12 md:h-14 md:w-14",
            ].join(" ")}
          >
            <img
              src={NBC_BADGE_SRC}
              alt={isJa ? "NBC Tokyo 2020 公式ベンダー関連資料" : "NBC Tokyo 2020 Official Olympics Vendor"}
              className={[
                "object-contain",
                placement === "hero"
                  ? "h-12 w-12 md:h-16 md:w-16"
                  : "h-10 w-10 md:h-12 md:w-12",
              ].join(" ")}
              loading="lazy"
              draggable={false}
            />
          </button>

          {/* Hover tooltip — rendered ABOVE the icon, brand purple pill,
              left-aligned to the icon so it never gets clipped by the
              host card's left edge. Arrow points down to the icon. */}
          <span
            role="tooltip"
            data-testid={`${testId}-badge-tooltip`}
            className="pointer-events-none absolute left-0 bottom-full mb-2.5 z-30 whitespace-nowrap rounded-full bg-[#5B2E83] text-white text-[11px] font-semibold tracking-tight px-3 py-1.5 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 transition-all duration-200 shadow-[0_10px_25px_-12px_rgba(91,46,131,0.6)]"
          >
            {isJa ? "クリックで公式推薦状を表示" : "Click to view the official letter"}
            <span
              aria-hidden
              className="absolute left-7 -bottom-1 w-2 h-2 rotate-45 bg-[#5B2E83]"
            />
          </span>
        </span>

        {/* Inline summary on the right of the icon */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-left max-w-[230px] md:max-w-[300px]"
        >
          <div className="text-[10px] tracking-[0.2em] font-mono-display uppercase font-bold text-[#5B2E83]">
            {isJa ? "NBC オリンピックスベンダー" : "NBC Olympics Vendor"}
          </div>
          <div className="text-[12px] font-semibold text-[#0F1D2D] tracking-tight leading-snug mt-0.5">
            {isJa
              ? "東京2020で200台超のETCカードを提供"
              : "Provided 200+ ETC cards for the Tokyo 2020 Games"}
          </div>
        </button>
      </div>

      {open && typeof document !== "undefined" && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={isJa ? "NBC Tokyo 2020 公式書簡" : "NBC Tokyo 2020 official letter"}
          data-testid={`${testId}-badge-modal`}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#5B2E83]/85 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          {/* Document-only modal — owner directive Feb 2026: open at
              ~90% of viewport height, document only (no chrome above or
              below). Rendered via portal to document.body so any
              ancestor `transform`/`translate` doesn't hijack the fixed
              positioning (caused the letter to overflow above the
              viewport on the Testimonials page). */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`${testId}-badge-modal-close`}
            aria-label={isJa ? "閉じる" : "Close"}
            className="absolute top-4 right-4 md:top-6 md:right-6 h-11 w-11 rounded-full bg-white ring-1 ring-[#E2E8F0] inline-flex items-center justify-center hover:bg-[#F1F5F9] transition-colors z-[90] shadow-lg"
          >
            <X className="h-5 w-5 text-[#0F1D2D]" strokeWidth={2.25} />
          </button>
          <img loading="lazy" decoding="async"
            src={NBC_LETTER_SRC}
            alt={
              isJa
                ? "NBC Olympicsからの推薦状"
                : "Recommendation letter from NBC Olympics"
            }
            onClick={(e) => e.stopPropagation()}
            className="rounded-xl shadow-2xl bg-white"
            style={{
              height: "90vh",
              width: "auto",
              maxWidth: "95vw",
              objectFit: "contain",
              display: "block",
            }}
            loading="eager"
            data-testid={`${testId}-badge-letter-img`}
          />
        </div>,
        document.body
      )}
    </>
  );
}
